import { ConfirmationModal } from 'views/components';
import PropTypes from 'prop-types';
import { configActions } from 'core/Config';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
    ButtonFooter,
    Checkbox,
    Container,
    ExpandButton,
    FormGroup,
    IconButton,
    Input,
    Modal,
    SelectSearch,
    Table,
    TagList,
    Text
} from 'mol-ui';
import React, { useEffect, useState } from 'react';
import {
    useArray,
    useDispatch,
    useObjectState,
    useSelector,
    useTableInput
} from 'hooks';

const DEFAULT_FORM = {
    content: '',
    id: '',
    order: 1,
    starting_point: 0,
    step: '',
    title: '',
    type: 'text',
    value: ''
};

const NODE_TYPES = {
    COMBO: 'combo',
    DATE: 'date',
    FILE: 'file',
    FILE_LIST: 'file_list',
    INPUT: 'input',
    MULTI_OPTION: 'multi_option',
    PICKER: 'picker',
    PROCESS: 'process',
    REDIRECT: 'redirect',
    SCORE: 'score',
    SUBMIT: 'submit',
    TEXT: 'text'
};

const TYPE_OPTIONS = Object.keys(NODE_TYPES).map(key => ({ label: NODE_TYPES[key], value: NODE_TYPES[key] }));

const NodeModal = ({
    active,
    data,
    onToggle
}) => {
    
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { dangerColor } = useTheme();
    const { featureConfigurationProcessing } = useSelector('config');
    const [currentNode, setCurrentNode] = useState('');
    const [deleteModal, setDeleteModal] = useState(false);
    const [form, setForm, setFormValue] = useObjectState(DEFAULT_FORM);
    const [nodes, setNodes, { includes, push, remove }] = useArray([]);
    const [optionsInput, options, setOptions] = useTableInput([], {
        config: ['key', 'label'].map(e => ({ flex: 3, key: e, label: e }))
    });

    useEffect(() => {
        if (!active) return setForm(DEFAULT_FORM);
        setDeleteModal(false);
        setForm({ ...DEFAULT_FORM, ...data });
        setOptions(data?.options ?? []);
        setNodes((data?.nodes ?? []).map(({ id }) => id));
    }, [data]);

    const handleAddNode = () => {
        if (!currentNode || includes(Number(currentNode))) return;
        push(Number(currentNode));
        setCurrentNode('');
    };

    const handleDelete = result => {
        if (result === 'cancel') return setDeleteModal(false);
        dispatch(configActions.deleteChatbotNode(data?.id));
    };

    const handleSubmit = () => {
        const data = {
            ...form,
            content_en: form?.content ?? '',
            content_es: form?.content ?? '',
            nodes,
            options: form?.type !== NODE_TYPES.MULTI_OPTION ? [] : options,
            position_x: form?.position_x,
            position_y: form?.position_y,
            starting_point: form?.starting_point ? 1 : 0,
            title_en: form?.title ?? '',
            title_es: form?.title ?? ''
        };
        dispatch(configActions.upsertChatbotNode(data));
    };

    const disabledInputs = featureConfigurationProcessing;
    const parsedNodes = nodes.map(node => ({ label: node.toString() }));

    return (
        <Modal active={ active } width="600px">
            <Container overflowY="auto" width="100%">
                {
                    !!form?.id &&
                    <Container flexDirection="column" margin="0px 0px 10px 0px">
                        <Container alignItems="flex-end">
                            <Text fontSize="9px">{ `${ form?.position_x }.x | ${ form.position_y }.y` }</Text>
                        </Container>
                        <Container alignItems="center" flexDirection="row" gap="5px" justifyContent="center">
                            <Text bold fontSize="14px" margin="0px" textAlign="center">{ form?.id?.toString() }</Text>
                            <IconButton
                                color={ dangerColor }
                                icon="delete"
                                size={ 16 }
                                onClick={ () => setDeleteModal(true) }
                            />
                        </Container>
                        <ConfirmationModal
                            actionHandler={ handleDelete }
                            active={ deleteModal }
                            loading={ featureConfigurationProcessing }
                            title={ t('¿Quieres eliminar el nodo seleccionado?') }
                        />
                    </Container>
                }
                { !form?.id && <Text bold fontSize="14px" margin="0px" textAlign="center">{ form?.feature }</Text> }
                <FormGroup label={ t('Título') } margin="0px 0px 10px 0px">
                    <Input
                        disabled={ disabledInputs }
                        value={ form?.title ?? '' }
                        width="100%"
                        onChange={ value => setFormValue('title', value) }
                    />
                </FormGroup>
                <FormGroup label={ t('Contenido') } margin="0px 0px 10px 0px">
                    <Input
                        disabled={ disabledInputs }
                        value={ form?.content ?? '' }
                        width="100%"
                        onChange={ value => setFormValue('content', value) }
                    />
                </FormGroup>
                <FormGroup label={ t('Tipo') } margin="0px 0px 10px 0px">
                    <SelectSearch
                        disabled={ disabledInputs }
                        fontSize="14px"
                        margin="0px 0px 10px 0px"
                        maxMenuHeight={ 200 }
                        options={ TYPE_OPTIONS }
                        value={ form?.type }
                        width="100%"
                        onChange={ value => setFormValue('type', value) }
                    />
                </FormGroup>
                {
                    form?.type === NODE_TYPES.MULTI_OPTION &&
                    <Table
                        { ...optionsInput }
                        margin="0px 0px 10px 0px"
                        minWidth="auto"
                    />
                }
                <FormGroup label={ t('Step') } margin="0px 0px 10px 0px">
                    <Input
                        disabled={ disabledInputs }
                        value={ form?.step ?? '' }
                        width="100%"
                        onChange={ value => setFormValue('step', value) }
                    />
                </FormGroup>
                <FormGroup label={ t('Value') } margin="0px 0px 10px 0px">
                    <Input
                        disabled={ disabledInputs }
                        value={ form?.value ?? '' }
                        width="100%"
                        onChange={ value => setFormValue('value', value) }
                    />
                </FormGroup>
                <Container flexDirection="row" gap="10px">
                    <Container flex="1">
                        <FormGroup label={ t('Nodos') }>
                            <Container flexDirection="row" gap="10px">
                                <Input
                                    disabled={ disabledInputs }
                                    value={ currentNode }
                                    width="100%"
                                    onChange={ setCurrentNode }
                                    onEnterPress={ handleAddNode }
                                />
                                <ExpandButton
                                    circle
                                    disabled={ includes(Number(currentNode)) || disabledInputs }
                                    icon="add"
                                    size={ 40 }
                                    onClick={ handleAddNode }
                                />
                            </Container>
                        </FormGroup>
                    </Container>
                    <Container flex="1">
                        <FormGroup label={ t('Orden') }>
                            <Container>
                                <Input
                                    disabled={ disabledInputs }
                                    value={ form?.order }
                                    width="100%"
                                    onChange={ value => setFormValue('order', value) }
                                />
                            </Container>
                        </FormGroup>
                    </Container>
                </Container>
                <TagList
                    items={ parsedNodes }
                    margin="0px 0px 10px 0px"
                    readOnly={ disabledInputs }
                    onRemove={ ({ label }) => remove(Number(label)) }
                />
                <Checkbox
                    checked={ !!form?.starting_point }
                    label={ t('Inicial') }
                    onClick={ value => setFormValue('starting_point', value ? 1 : 0) }
                />
                <ButtonFooter margin="20px 0px 0px 0px">
                    <ExpandButton
                        danger
                        caption={ t('Cancelar') }
                        size={ 100 }
                        onClick={ onToggle }
                    />
                    <ExpandButton
                        caption={ t('Guardar') }
                        loading={ disabledInputs }
                        size={ 100 }
                        onClick={ handleSubmit }
                    />
                </ButtonFooter>
            </Container>
        </Modal>
    );
};

NodeModal.propTypes = {
    active: PropTypes.bool,
    data: PropTypes.object,
    onToggle: PropTypes.func
};

export default NodeModal;
